import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  FaEdit,
  FaArrowLeft,
  FaSearch,
  FaSpinner,
  FaChevronDown,
  FaPlus,
} from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import useAuth from "../../hooks/useAuthContext";
import { formatarDia } from "../../services/conversaoTimer";
import "./admPatrocinadoresList.css";

const LABEL_TIPO = {
  convidado: "Convidado",
  patrocinador: "Patrocinador",
  participante_patrocinador: "Participante patrocinador",
  administracao: "Administração",
  administracao_dash: "Admin. dashboard",
  recepcao: "Recepção",
};

function labelTipo(tipo) {
  return LABEL_TIPO[tipo] || tipo || "—";
}

function linkEditarUsuario(usuario) {
  if (!usuario?.id) return null;
  if (
    usuario.tipo === "convidado" ||
    usuario.tipo === "participante_patrocinador" ||
    usuario.tipo === "recepcao" ||
    usuario.tipo === "administracao" ||
    usuario.tipo === "administracao_dash"
  ) {
    return `/private/adm/convidados/editar/${usuario.id}`;
  }
  if (usuario.tipo === "patrocinador") {
    const idPatro =
      usuario.infoPatrocinador?.id ?? usuario.info_patrocinador?.id;
    if (idPatro) return `/private/adm/patrocinadores/editar/${idPatro}`;
  }
  return null;
}

const AdmUsuarios = () => {
  const navigate = useNavigate();
  const { getUsuariosLista } = useApp();
  const { usuario } = useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [filtrosTipo, setFiltrosTipo] = useState([]);
  const [tipoMenuAberto, setTipoMenuAberto] = useState(false);
  const tipoMenuRef = useRef(null);

  useEffect(() => {
    if (usuario && usuario.tipo !== "administracao") {
      navigate("/private/adm");
    }
  }, [usuario, navigate]);

  const carregarUsuarios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const lista = await getUsuariosLista();
      setUsuarios(Array.isArray(lista) ? lista : []);
    } catch (err) {
      console.error("Erro ao carregar usuários:", err);
      setError("Erro ao carregar lista de usuários");
    } finally {
      setIsLoading(false);
    }
  }, [getUsuariosLista]);

  useEffect(() => {
    if (usuario?.tipo === "administracao") {
      carregarUsuarios();
    }
  }, [usuario, carregarUsuarios]);

  useEffect(() => {
    if (!tipoMenuAberto) return undefined;
    const fecharAoClicarFora = (evento) => {
      if (
        tipoMenuRef.current &&
        !tipoMenuRef.current.contains(evento.target)
      ) {
        setTipoMenuAberto(false);
      }
    };
    document.addEventListener("mousedown", fecharAoClicarFora);
    return () => document.removeEventListener("mousedown", fecharAoClicarFora);
  }, [tipoMenuAberto]);

  const alternarFiltroTipo = useCallback((tipo) => {
    setFiltrosTipo((anteriores) =>
      anteriores.includes(tipo)
        ? anteriores.filter((t) => t !== tipo)
        : [...anteriores, tipo],
    );
  }, []);

  const tiposDisponiveis = useMemo(() => {
    const set = new Set(
      usuarios.map((u) => u.tipo).filter(Boolean),
    );
    return [...set].sort((a, b) =>
      labelTipo(a).localeCompare(labelTipo(b), "pt-BR"),
    );
  }, [usuarios]);

  const usuariosFiltrados = useMemo(() => {
    let resultado = usuarios;
    if (filtro.trim()) {
      const termo = filtro.toLowerCase().trim();
      resultado = resultado.filter((u) => {
        const nome = (u.nome || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const empresa = (u.empresa || "").toLowerCase();
        return (
          nome.includes(termo) ||
          email.includes(termo) ||
          empresa.includes(termo)
        );
      });
    }
    if (filtrosTipo.length > 0) {
      resultado = resultado.filter((u) => filtrosTipo.includes(u.tipo));
    }
    return resultado;
  }, [usuarios, filtro, filtrosTipo]);

  const resumoFiltrosTipo = useMemo(() => {
    if (!filtrosTipo.length) return "Todos os tipos";
    return filtrosTipo.map((t) => labelTipo(t)).join(", ");
  }, [filtrosTipo]);

  if (usuario?.tipo !== "administracao") {
    return null;
  }

  return (
    <div className="container-adm-main">
      <div className="apl-page">
        <header className="apl-header" aria-label="Cabeçalho da listagem">
          <div className="apl-header-text">
            <h1>Usuários</h1>
            <p>
              Visualize todos os usuários registrados no sistema por tipo,
              e-mail e data de cadastro.
            </p>
          </div>

          <div className="apl-toolbar">
            <div className="apl-search apl-search--wide">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Buscar por nome, e-mail ou empresa…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                disabled={isLoading}
                aria-label="Buscar usuário"
              />
            </div>

            <div className="apl-filter-multi" ref={tipoMenuRef}>
              <button
                type="button"
                className={`apl-filter-pill apl-filter-multi-trigger${
                  filtrosTipo.length ? " is-active" : ""
                }`}
                onClick={() => setTipoMenuAberto((aberto) => !aberto)}
                disabled={isLoading}
                aria-expanded={tipoMenuAberto}
                aria-haspopup="listbox"
                aria-label="Filtrar por tipo de usuário"
              >
                <span>Tipo</span>
                {filtrosTipo.length > 0 ? (
                  <span className="apl-filter-multi-badge">
                    {filtrosTipo.length}
                  </span>
                ) : null}
                <FaChevronDown
                  size={10}
                  aria-hidden
                  className={`apl-filter-multi-chevron${
                    tipoMenuAberto ? " is-open" : ""
                  }`}
                />
              </button>
              {tipoMenuAberto ? (
                <div
                  className="apl-filter-multi-menu"
                  role="listbox"
                  aria-multiselectable="true"
                  aria-label="Tipos de usuário"
                >
                  {tiposDisponiveis.map((t) => (
                    <label key={t} className="apl-filter-multi-option">
                      <input
                        type="checkbox"
                        checked={filtrosTipo.includes(t)}
                        onChange={() => alternarFiltroTipo(t)}
                      />
                      <span>{labelTipo(t)}</span>
                    </label>
                  ))}
                  {filtrosTipo.length > 0 ? (
                    <button
                      type="button"
                      className="apl-filter-multi-clear"
                      onClick={() => setFiltrosTipo([])}
                    >
                      Limpar seleção
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <Link
              to="/private/adm/convidados/novo"
              className="apl-btn apl-btn--primary"
            >
              <FaPlus size={14} aria-hidden />
              Novo usuário
            </Link>

            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? (
            <div className="apl-alert" role="alert">
              {error}
            </div>
          ) : null}

          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando usuários…</span>
              </div>
            </div>
          ) : (
            <section className="apl-card" aria-label="Tabela de usuários">
              <div className="apl-table-wrap">
                {usuariosFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {usuarios.length === 0
                        ? "Nenhum usuário cadastrado"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {usuarios.length === 0
                        ? "Não há usuários registrados no sistema."
                        : "Ajuste a busca ou o filtro de tipo."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th className="apl-cell-logo" scope="col">
                          Avatar
                        </th>
                        <th scope="col">Nome</th>
                        <th scope="col">E-mail</th>
                        <th scope="col">Tipo</th>
                        <th scope="col">Empresa</th>
                        <th scope="col">Cadastro</th>
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosFiltrados.map((u) => {
                        const editar = linkEditarUsuario(u);
                        return (
                          <tr key={u.id}>
                            <td className="apl-cell-logo">
                              {u.avatar ? (
                                <img
                                  className="apl-logo-thumb apl-avatar-thumb"
                                  src={u.avatar}
                                  alt=""
                                />
                              ) : (
                                <div className="apl-logo-placeholder">—</div>
                              )}
                            </td>
                            <td className="apl-cell-name">{u.nome || "—"}</td>
                            <td className="apl-cell-email">{u.email || "—"}</td>
                            <td>
                              <span className="apl-tag-type">
                                {labelTipo(u.tipo)}
                              </span>
                            </td>
                            <td>{u.empresa || "—"}</td>
                            <td>
                              <span
                                className="apl-stat"
                                title="Data de cadastro no sistema"
                              >
                                {formatarDia(u.created_at) || "—"}
                              </span>
                            </td>
                            <td>
                              <div className="apl-actions">
                                {editar ? (
                                  <Link to={editar}>
                                    <FaEdit size={12} aria-hidden />
                                    Editar
                                  </Link>
                                ) : (
                                  <span className="apl-stat-muted">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {usuariosFiltrados.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    Exibindo{" "}
                    <strong>{usuariosFiltrados.length}</strong>{" "}
                    {usuariosFiltrados.length === 1
                      ? "usuário"
                      : "usuários"}
                    {(filtro || filtrosTipo.length > 0) && (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (de {usuarios.length} no total)
                        </span>
                      </>
                    )}
                  </span>
                  <span title={resumoFiltrosTipo}>
                    {filtrosTipo.length > 0
                      ? `Filtro: ${resumoFiltrosTipo}`
                      : resumoFiltrosTipo}
                  </span>
                </footer>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmUsuarios;
